import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, User, Clock, Trash2 } from 'lucide-react';
import { apiFetch } from '../../../utils/api';

interface ProjectTasksProps {
    projectId: string;
}

const ProjectTasks: React.FC<ProjectTasksProps> = ({ projectId }) => {
    const [tasks, setTasks] = useState<any[]>([]);
    const [milestones, setMilestones] = useState<any[]>([]);
    const [admins, setAdmins] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);

    const [newTask, setNewTask] = useState({
        name: '', description: '', milestone_id: '', assigned_to: '', priority: 'Medium', estimated_hours: '', due_date: ''
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [tasksRes, milestonesRes, adminsRes] = await Promise.all([
                apiFetch(`/project-delivery/tasks?project_id=${projectId}`),
                apiFetch(`/project-delivery/milestones?project_id=${projectId}`),
                apiFetch(`/admin/sub-admins`)
            ]);

            if (tasksRes.ok) setTasks(await tasksRes.json());
            if (milestonesRes.ok) setMilestones(await milestonesRes.json());
            if (adminsRes.ok) setAdmins(await adminsRes.json());
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, [projectId]);

    useEffect(() => {
        fetchData();
    }, [projectId, fetchData]);

    const handleAddTask = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/project-delivery/tasks', {
                method: 'POST',
                body: JSON.stringify({ ...newTask, project_id: projectId })
            });
            if (res.ok) {
                setShowAddModal(false);
                setNewTask({ name: '', description: '', milestone_id: '', assigned_to: '', priority: 'Medium', estimated_hours: '', due_date: '' });
                fetchData();
            }
        } catch (err) { console.error(err); }
    };

    const updateStatus = async (taskId: string, status: string) => {
        try {
            const res = await apiFetch(`/project-delivery/tasks/${taskId}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status })
            });
            if (res.ok) fetchData();
        } catch (err) { console.error(err); }
    };

    const handleDeleteTask = async (taskId: string) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;
        try {
            const res = await apiFetch(`/project-delivery/tasks/${taskId}`, { method: 'DELETE' });
            if (res.ok) fetchData();
        } catch (err) { console.error(err); }
    };

    const columns = ['To Do', 'In Progress', 'Testing', 'Completed'];

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin text-blue-500" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800">Task Board</h3>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-semibold"
                >
                    <Plus size={16} /> Add Task
                </button>
            </div>

            <div className="flex gap-6 overflow-x-auto pb-4">
                {columns.map(col => (
                    <div key={col} className="flex-1 min-w-[300px] bg-gray-50/50 rounded-2xl p-4 border border-gray-100 h-fit">
                        <div className="flex items-center justify-between mb-4 px-2">
                            <h4 className="font-bold text-gray-600 uppercase text-xs tracking-wider">{col}</h4>
                            <span className="bg-gray-200 text-gray-600 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                {tasks.filter(t => t.status === col).length}
                            </span>
                        </div>
                        <div className="space-y-3">
                            {tasks.filter(t => t.status === col).map((task, i) => (
                                <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition cursor-default">
                                    <div className="flex justify-between items-start mb-2">
                                        <h5 className="font-bold text-gray-800 text-sm leading-tight">{task.name}</h5>
                                        <div className="flex items-center gap-1">
                                            <div className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${task.priority === 'Critical' ? 'bg-red-100 text-red-600' :
                                                task.priority === 'High' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'
                                                }`}>
                                                {task.priority}
                                            </div>
                                            <button
                                                onClick={() => handleDeleteTask(task.id)}
                                                className="p-1 text-gray-400 hover:text-red-500 rounded transition"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-4 line-clamp-2">{task.description}</p>

                                    <div className="flex items-center justify-between mt-auto">
                                        <div className="flex items-center gap-2 text-[10px] text-gray-400 font-medium">
                                            <div className="flex items-center gap-1">
                                                <User size={12} />
                                                <span>{admins.find(a => a.id === task.assigned_to)?.username || 'Unassigned'}</span>
                                            </div>
                                            <div className="flex items-center gap-1 ml-2">
                                                <Clock size={12} />
                                                <span>{task.estimated_hours}h</span>
                                            </div>
                                        </div>
                                        <select
                                            className="text-[10px] bg-gray-50 border-none outline-none font-bold text-blue-600 rounded"
                                            value={task.status}
                                            onChange={(e) => updateStatus(task.id, e.target.value)}
                                        >
                                            {columns.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
                    <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
                        <h3 className="text-xl font-bold mb-4">Create New Task</h3>
                        <form onSubmit={handleAddTask} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Task Name</label>
                                <input className="w-full border p-2 rounded-lg" value={newTask.name} onChange={e => setNewTask({ ...newTask, name: e.target.value })} required />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                                <textarea className="w-full border p-2 rounded-lg" rows={3} value={newTask.description} onChange={e => setNewTask({ ...newTask, description: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Milestone</label>
                                    <select className="w-full border p-2 rounded-lg" value={newTask.milestone_id} onChange={e => setNewTask({ ...newTask, milestone_id: e.target.value })}>
                                        <option value="">No Milestone</option>
                                        {milestones.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Assigned To</label>
                                    <select className="w-full border p-2 rounded-lg" value={newTask.assigned_to} onChange={e => setNewTask({ ...newTask, assigned_to: e.target.value })}>
                                        <option value="">Unassigned</option>
                                        {admins.map(a => <option key={a.id} value={a.id}>{a.username}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Priority</label>
                                    <select className="w-full border p-2 rounded-lg" value={newTask.priority} onChange={e => setNewTask({ ...newTask, priority: e.target.value })}>
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                        <option value="Critical">Critical</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Est. Hours</label>
                                    <input type="number" className="w-full border p-2 rounded-lg" value={newTask.estimated_hours} onChange={e => setNewTask({ ...newTask, estimated_hours: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date</label>
                                    <input type="date" className="w-full border p-2 rounded-lg" value={newTask.due_date} onChange={e => setNewTask({ ...newTask, due_date: e.target.value })} />
                                </div>
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 border rounded-lg font-bold">Cancel</button>
                                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-bold">Create Task</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectTasks;
